
   import { defineComponent } from "vue";
   import { getAuthToken } from "@/security";
   import { PassengerView, PassengerUpdate, Trip } from "@/ApiInterfaces";
   import CmsHeader from "@/components/CmsHeader.vue";
   import CmsBreadcrumbs from "@/components/CmsBreadcrumbs.vue";
   import StateSelect from "@/components/StateSelect.vue";
   import ModalLoading from "@/components/ModalLoading.vue";
   import ModalErrorMessage from "@/components/ModalErrorMessage.vue";
   import ModalInfoMessage from "@/components/ModalInfoMessage.vue";
   import { groupBy, formatPhoneNumber, generateGuid } from "@/main";
   import { parseApiError, fieldError, getFieldErrorMsg, hasFieldError } from "@/apiErrorHandling";

   interface RLData {
      trip: null | Trip;
      passengers: PassengerView[];
      draggingPax: null | PassengerView;
      draggingOverElement: null | HTMLElement;
      draggingOverCount: number;
      loading: boolean;
      errorMessage: null | string;
      infoMessage: null | string;
      fieldErrors: null | fieldError[];
      showingPaxDetailsId: null | string;
      paxDetailsTimer: null | number;
      unsavedChanges: boolean;
      dataLoaded: boolean;
      animateSaved: boolean;
   }

   export default defineComponent({
      name: 'RoomingList',
      data(): RLData {
         return {
            trip: null,
            passengers: [],
            draggingPax: null,
            draggingOverElement: null,
            draggingOverCount: 0,
            loading: false,
            errorMessage: null,
            infoMessage: null,
            fieldErrors: null,
            showingPaxDetailsId: null,
            paxDetailsTimer: null,
            unsavedChanges: false,
            dataLoaded: false,
            animateSaved: false
         }
      },
      components: {
         ModalLoading,
         ModalErrorMessage,
         ModalInfoMessage,
         CmsHeader,
         CmsBreadcrumbs,
         StateSelect,
      },
      directives: {
         focus: {
            mounted(el) {
               el.focus();
            },
         },
      },
      watch: {
         passengers: {
            handler(oldVal, newVal) {
               if (this.dataLoaded) {
                  this.unsavedChanges = true;
               }
            },
            deep: true
         }
      },
      beforeRouteLeave(to, from, next) {
         const answer = this.unsavedChanges ? window.confirm('You have unsaved changes that will be lost. Are you sure you wish to leave?') : true;
         if (answer) {
            next()
         } else {
            next(false)
         }
      },
      created() {
         document.title = `Rooming List for Tour # ${this.$route.params.tourId} | Group Trips`;

         window.addEventListener("beforeunload", (e) => {
            if (this.unsavedChanges) {
               e.preventDefault()
               const message = "You have unsaved changes that will be lost. Are you sure you wish to leave?"
               e.returnValue = message
               return message
            }
         })

         const token = getAuthToken();

         if (token) {
            try {
               fetch(
                  `/api/gttrips/cms/${this.$route.params.tourId}`,
                  {
                     headers: {
                        "X-DT-TOKEN": token
                     }
                  }
               )
                  .then((r) => {
                     if (r.ok) {
                        r.json()
                           .then((json) => {
                              this.trip = json as Trip;
                              document.title = `Rooming List for Tour # ${this.trip.tourId} - ${this.trip.tourName} - ${this.tripDates} | Group Trips`;

                              fetch(
                                 `/api/passengers/${this.$route.params.tourId}`,
                                 {
                                    headers: {
                                       "X-DT-TOKEN": token!
                                    }
                                 }
                              )
                                 .then(r => {
                                    if (r.ok) {
                                       r.json()
                                          .then(json => {
                                             const pj = json as PassengerView[];
                                             pj.forEach(p => {
                                                if (!p.clientId) p.clientId = generateGuid();
                                             });
                                             this.passengers = pj;
                                             this.$nextTick(() => { this.dataLoaded = true; });
                                          });
                                    } else {
                                       window.location.href = '/CMS/TripListPassengers';
                                    }
                                 })
                                 .catch(e => {
                                    console.error(e.toString());
                                    window.location.href = '/CMS/TripListPassengers';
                                 });
                              return;

                           })
                           .catch((e) => {
                              console.error(e.toString());
                              window.location.href = '/CMS/Login';
                           });
                     } else {
                        // Contact not found so go to the login page
                        window.location.href = '/CMS/Login';
                     }
                  })
                  .catch((e) => {
                     console.error(e.toString());
                     window.location.href = '/CMS/Login';
                  });
            } catch (e) {
               console.error(e.toString());
               window.location.href = '/CMS/Login';
            }
         }
      },
      computed: {
         rooms(): { [key: string]: any[] } {
            return groupBy(this.passengers.filter(p => !p.cancelledDate), "room");
         },
         roomCount(): number {
            return Object.keys(groupBy(this.passengers.filter(p => !p.cancelledDate), "room")).length;
         },
         roomMax(): number {
            return this.passengers.filter(p => !p.cancelledDate)
               .map((p) => Number(p.room))
               .reduce((a: number, c: number) => (a > c ? a : c), 0);
         },
         paxCount(): number {
            return this.passengers.filter(p => !p.cancelledDate).length;
         },
         singleCount(): number {
            let count = 0;
            const rms = this.rooms;
            for (let roomNum in rms) {
               if (rms[roomNum].length == 1) count++;
            }
            return count;
         },
         doubleCount(): number {
            let count = 0;
            const rms = this.rooms;
            for (let roomNum in rms) {
               if (rms[roomNum].length == 2) count++;
            }
            return count;
         },
         tripleCount(): number {
            let count = 0;
            const rms = this.rooms;
            for (let roomNum in rms) {
               if (rms[roomNum].length == 3) count++;
            }
            return count;
         },
         tripDates(): string {
            if (this.trip) {
               return `${new Date(this.trip!.startDate.toString()).toLocaleDateString("en-US")} to ${new Date(this.trip!.returnDate.toString()).toLocaleDateString("en-US")}`;
            } else {
               return "";
            }
         }
      },
      methods: {
         addPassenger(roomNum: number) {
            const newPax: PassengerView = {
               clientId: generateGuid(),
               room: roomNum,
               name: "",
               twoBeds: false,
               hasWaiver: false
            };
            this.passengers.push(newPax);
            this.showingPaxDetailsId = newPax.clientId;
         },
         removePassenger(pax: PassengerView): void {
            let idx = this.passengers.indexOf(pax);
            if (idx !== -1) {
               let name = pax.name ? pax.name : "New Passenger";
               if (confirm("Click OK to confirm removal of passenger " + name)) {
                  if (pax.passengerGuid) {
                     // If they have a Guid then it has been saved, so cancel rather than remove
                     pax.cancelledDate = new Date(new Date().toDateString());
                  } else {
                     this.passengers.splice(idx, 1);
                  }
               }
            }
         },
         saveChanges() {
            this.animateSaved = false;
            this.fieldErrors = null;
            this.loading = true;

            const token = getAuthToken() ?? "";

            try {
               fetch(
                  `/api/passengers/${this.$route.params.tourId}`,
                  {
                     method: "put",
                     headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "X-DT-TOKEN": token
                     },
                     body: JSON.stringify(this.passengers.map(p => p as PassengerUpdate)),
                  }
               )
                  .then(r => {
                     if (r.ok) {
                        r.json()
                           .then(json => {
                              const pj = json as PassengerView[];

                              // Update local passenger objects with any server side controlled fields
                              pj.forEach(p => {
                                 if (p.clientId) {
                                    let pe = this.passengers.find(x => x.clientId == p.clientId);
                                    if (pe) {
                                       pe.passengerGuid = p.passengerGuid;
                                       pe.modifiedDate = p.modifiedDate;
                                    }
                                 }
                              });
                              this.loading = false;
                              this.animateSaved = true;
                              this.$nextTick(() => { this.unsavedChanges = false; });
                           });
                     } else if (r.status == 403) {
                        this.loading = false;
                        this.errorMessage = "This rooming list cannot be edited at this time.";
                     } else if (r.status == 400) {
                        r.text().then((t) => {
                           const errs = parseApiError(t);
                           for (const err of errs.fieldErrors) {
                              if (err.index != null && err.index >= 0 && err.index < this.passengers.length) {
                                 err.clientId = this.passengers[err.index].clientId;
                              }
                           }
                           // Setting these will automatically update the disply and show an error
                           this.loading = false;
                           this.fieldErrors = errs.fieldErrors;
                           this.errorMessage = errs.message;
                        });
                     } else if (r.status == 404) {
                        // Trip not found 
                        this.unsavedChanges = false;
                        window.location.href = '/CMS/TripListPassengers';
                     } else {
                        this.loading = false;
                        this.errorMessage = "Something went wrong. Your changes may not have saved, sorry.";
                     }
                  })
                  .catch(e => {
                     this.loading = false;
                     console.error(e.toString());
                     this.errorMessage = "Something went wrong. Your changes may not have saved, sorry.";
                  });
            } catch (e) {
               this.loading = false;
               console.error(e.toString());
               this.errorMessage = "Something went wrong. Your changes may not have saved, sorry.";
            }

         },
         formatPhone(phoneRaw: string): string {
            if (phoneRaw) {
               let formatted = "";
               if (phoneRaw.length > 6)
                  formatted = `(${phoneRaw.substring(0, 3)}) ${phoneRaw.substring(
                     3,
                     6
                  )}-${phoneRaw.substring(6, 10)}`;
               else
                  formatted = `(${phoneRaw.substring(0, 3)}) ${phoneRaw.substring(
                     3,
                     6
                  )}`;

               return formatted;
            } else return "";
         },
         roomPaxCount(roomNum: number): number {
            return this.passengers.filter((p) => !p.cancelledDate && p.room == roomNum).length;
         },
         roomType(paxCount: number): string {
            switch (paxCount) {
               case 1:
                  return "Single";
               case 2:
                  return "Double";
               case 3:
                  return "Triple";
               default:
                  return "N/A";
            }
         },
         startDrag(event: DragEvent, item: PassengerView) {
            if (event && event.dataTransfer && this.trip?.canEditRoomingList) {
               event.dataTransfer.dropEffect = "move";
               event.dataTransfer.effectAllowed = "move";
               this.draggingPax = item;
            }
         },
         dragOver(event: DragEvent, roomNum: number): void {
            if (event && event.dataTransfer) {
               this.draggingOverCount++;

               if (this.draggingPax) {
                  if (
                     this.draggingPax.room != roomNum &&
                     this.roomPaxCount(roomNum) < 3
                  ) {
                     if (event.target && event.target) {
                        let dragTarget = event.target as HTMLElement;
                        if (dragTarget.hasAttribute("dropzone")) {
                           dragTarget.style.backgroundColor = "lightgreen";
                           if (
                              this.draggingOverElement &&
                              this.draggingOverElement != dragTarget
                           ) {
                              this.draggingOverElement.style.backgroundColor = "";
                           }
                           this.draggingOverElement = dragTarget;
                        }
                     }
                     event.dataTransfer.dropEffect = "move";
                     event.dataTransfer.effectAllowed = "move";
                  }
                  event.preventDefault();
               }
            }
         },
         dragOut(event: DragEvent): void {
            if (event && event.target) {
               this.draggingOverCount--;
               if (this.draggingOverCount == 0) {
                  if (this.draggingOverElement)
                     this.draggingOverElement.style.backgroundColor = "";
               }
            }
         },
         dragDrop(roomNum: number): void {
            if (this.draggingPax) {
               if (
                  this.draggingPax.room != roomNum &&
                  this.roomPaxCount(roomNum) < 3
               ) {
                  if (this.draggingOverElement)
                     this.draggingOverElement.style.backgroundColor = "";
                  this.draggingPax.room = roomNum;
               }
            }
            this.draggingOverCount = 0;
         },
         dragEnd(): void {
            if (this.draggingOverElement) {
               this.draggingOverElement.style.backgroundColor = "";
               this.draggingOverElement = null;
            }
            this.draggingOverCount = 0;
         },
         showPaxDetails(el: HTMLElement, clientId: string) {
            if (this.paxDetailsTimer) {
               if (this.showingPaxDetailsId == clientId) {
                  clearTimeout(this.paxDetailsTimer);
                  this.paxDetailsTimer = null;
               }
            }

            el.style.zIndex = '10';
            this.showingPaxDetailsId = clientId;
         },
         hidePaxDetails(el: HTMLElement, clientId: string) {

            this.paxDetailsTimer = setTimeout(() => {
               if (this.showingPaxDetailsId == clientId) this.showingPaxDetailsId = null;

               this.paxDetailsTimer = null;

            }, 500);
         },
         cancelHidePaxDetails(el: HTMLElement, clientId: string) {
            if (this.paxDetailsTimer && this.showingPaxDetailsId == clientId) {
               el.style.zIndex = '10';
               clearTimeout(this.paxDetailsTimer);
               this.paxDetailsTimer = null;
            }
         },
         beforePaxDetailsFadeIn(el: HTMLElement) {
            if (el.previousElementSibling) (el.previousElementSibling as HTMLElement).style.zIndex = '10';
         },
         afterPaxDetailsFadeOut(el: HTMLElement) {
            if (el.previousElementSibling) (el.previousElementSibling as HTMLElement).style.zIndex = '';
         },
         hasWaiver(pax: PassengerView) {
            return pax.hasWaiver;
         },
         waiverSentNotSigned(pax: PassengerView) {
            return !pax.hasWaiver && pax.onlineWaiverRequestLastSent;
         },
         waiverNotSent(pax: PassengerView) {
            return !pax.hasWaiver && !pax.onlineWaiverRequestLastSent
         },
         waiverStatus(pax: PassengerView) {
            if (pax.hasWaiver) {
               if (pax.onlineWaiverSignDate)
                  return `Signed online on ${new Date(pax.onlineWaiverSignDate.toString()).toLocaleDateString('en-US')}`;
               else
                  return 'Signed paper waiver';
            } else if (pax.onlineWaiverRequestLastSent) {
               return `Waiver request sent on ${new Date(pax.onlineWaiverRequestLastSent.toString()).toLocaleDateString('en-US')} to ${pax.onlineWaiverRequestLastEmailTo}`;
            } else {
               return `Waiver not signed or sent`;
            }
         },
         canSendWaiver(pax: PassengerView): boolean {
            if (!pax.hasWaiver && pax.email && !pax.cancelledDate && pax.passengerGuid)
               return !this.unsavedChanges && !pax.hasWaiver;
            else
               return false;

         },
         daydiff(first: Date, second: Date) {
            // Take the difference between the dates and divide by milliseconds per day.
            // Round to nearest whole number to deal with DST.
            return Math.round((second.getDate() - first.getDate()) / (1000 * 60 * 60 * 24));
         },
         sendWaiverRequest(pax: PassengerView) {
            if (this.canSendWaiver(pax)) {
               this.sendWaiverRequestsByGuid([pax.passengerGuid!], false);
            }
         },
         sendWaiverRequestAll() {
            if (this.passengers && this.passengers.length > 0) {
               if (this.unsavedChanges) {
                  this.errorMessage = "Please save changes first.";
               } else {
                  const sendTo = this.passengers.filter(p => this.canSendWaiver(p as PassengerView) &&
                     (!p.onlineWaiverRequestLastSent || this.daydiff(new Date(p.onlineWaiverRequestLastSent!.toString()), new Date()) > 7))
                     .map(p => p.passengerGuid!);

                  if (sendTo && sendTo.length > 0) {
                     this.sendWaiverRequestsByGuid(sendTo, true);
                  } else {
                     this.errorMessage = "No passengers without a signed waiver have a valid email address or have had a waiver request email in the last 7 days. Click the individual waiver request links on each passenger to resend emails sooner than 7 days.";
                  }
               }
            } else {
               this.errorMessage = "No passengers are on this trip for sending waiver requests.";
            }
         },
         sendWaiverRequestsByGuid(guids: string[], showSentNames: boolean): void {
            this.loading = true;

            const token = getAuthToken() ?? "";

            try {
               fetch(
                  `/api/OnlineWaiver/${this.$route.params.tourId}/SendWaiverRequests`,
                  {
                     method: "post",
                     headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "X-DT-TOKEN": token
                     },
                     body: JSON.stringify(guids),
                  }
               )
                  .then(r => {
                     if (r.ok) {
                        r.json()
                           .then(json => {
                              const pj = json as PassengerView[];

                              let namesSent = "";

                              // Update local passenger objects with any server side controlled fields
                              pj.forEach(p => {
                                 let pe = this.passengers.find(x => x.passengerGuid == p.passengerGuid);
                                 if (pe) {
                                    pe.onlineWaiverRequestLastEmailTo = p.onlineWaiverRequestLastEmailTo;
                                    pe.onlineWaiverRequestLastSent = p.onlineWaiverRequestLastSent;
                                    pe.modifiedDate = p.modifiedDate;
                                 }
                                 namesSent += '\r\n' + p.name;
                              });
                              this.loading = false;
                              this.$nextTick(() => { this.unsavedChanges = false; });

                              if (pj.length == 0) {
                                 this.errorMessage = "No waiver request emails were sent. Please check the passenger(s) have a valid full name, valid email address and has not had a previous email request sent in the last 5 minutes."
                              } else if (showSentNames) {
                                 this.infoMessage = "Waiver request email successfully sent to the following passengers:\r\n" + namesSent;
                              }
                           });
                     } else if (r.status == 404) {
                        // Trip not found 
                        this.unsavedChanges = false;
                        window.location.href = '/CMS/TripListPassengers';
                     } else {
                        this.loading = false;
                        this.errorMessage = "Something went wrong. The waiver request emails may not have been sent, sorry.";
                     }
                  })
                  .catch(e => {
                     this.loading = false;
                     console.error(e.toString());
                     this.errorMessage = "Something went wrong. The waiver request emails may not have been sent, sorry.";
                  });
            } catch (e) {
               this.loading = false;
               console.error(e.toString());
               this.errorMessage = "Something went wrong. The waiver request emails may not have been sent, sorry.";
            }
         },
         canDownloadWaiver(pax: PassengerView) {
            if (pax.hasWaiver && pax.passengerGuid && pax.onlineWaiverSignDate)
               return true;
            else
               return false;
         },
         downloadWaiver(pax: PassengerView) {
            this.loading = true;

            const token = getAuthToken() ?? "";

            try {
               fetch(
                  `/api/onlinewaiver/${this.$route.params.tourId}/${pax.passengerGuid}`,
                  {
                     headers: {
                        "X-DT-TOKEN": token
                     }
                  }
               )
                  .then(r => {
                     this.loading = false;

                     if (r.ok) {
                        r.blob().then(file => {
                           const a = document.createElement("a");
                           const fileUrl = window.URL.createObjectURL(file);
                           a.href = fileUrl;
                           a.download = `SignedWaiver-${this.$route.params.tourId}-${pax.name.replace(/[^0-9a-zA-Z]/, "")}.pdf`
                           a.click();
                        }).catch(e => {
                           console.error(e.toString());
                           this.errorMessage = "Something went wrong. Please try again later.";
                        })
                     } else {
                        this.errorMessage = "Something went wrong. Please try again later.";
                     }
                  })
            } catch (e) {
               this.loading = false;
               console.error(e.toString());
               this.errorMessage = "Something went wrong. Please try again later.";
            }
         }
      }
   });

